/**
 * File vocabulary: the closed value sets that say what a file is (kind), how
 * far it may travel (sensitivity), what condition it shows, and how a label
 * was set and reviewed. One home for the platform, the webapp and Birdie.
 * Plan: docs/plans/file-classification/ (01-vocabulary.md); human copy:
 * docs/platform/files/vocabulary.md.
 *
 * Storage is TEXT parsed with z.enum at every boundary (OD-21, DL-32), never a
 * Postgres enum or CHECK constraint. Purposes and audiences (why a file sits in
 * a placement, and who sees it there) join this module with their first
 * reader (file-classification Phase 3).
 */

/** What a file is, judged from its content (01-vocabulary § Kinds). */
export const FILE_KINDS = [
  "defect",
  "equipment",
  "nameplate",
  "area_overview",
  "work_in_progress",
  "receipt",
  "screenshot",
  "call_recording",
  "listing_photo",
  "avatar_or_logo",
  "other_visual",
  "vendor_quote",
  "vendor_invoice",
  "vendor_compliance",
  "birdwatch_document",
  "inspection_report",
  "home_reference",
  "contract",
  "correspondence",
  "other_document",
  "voice_note",
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** The kinds the classifier may output: every kind but `voice_note` and `listing_photo`, which only rules set. */
export const CLASSIFIABLE_FILE_KINDS = [
  "defect",
  "equipment",
  "nameplate",
  "area_overview",
  "work_in_progress",
  "receipt",
  "screenshot",
  "call_recording",
  "avatar_or_logo",
  "other_visual",
  "vendor_quote",
  "vendor_invoice",
  "vendor_compliance",
  "birdwatch_document",
  "inspection_report",
  "home_reference",
  "contract",
  "correspondence",
  "other_document",
] as const;
export type ClassifiableFileKind = (typeof CLASSIFIABLE_FILE_KINDS)[number];

/** The values a file is read with once `unreviewed` is resolved. Narrowest last. */
export const READING_SENSITIVITIES = [
  "open",
  "internal",
  "confidential",
] as const;
export type ReadingSensitivity = (typeof READING_SENSITIVITIES)[number];

/** How far a file may travel, whatever its placements say. `unreviewed`: nobody (no rule, no person) has decided yet. */
export const FILE_SENSITIVITIES = [
  "unreviewed",
  ...READING_SENSITIVITIES,
] as const;
export type FileSensitivity = (typeof FILE_SENSITIVITIES)[number];

export const FILE_KIND_META: Readonly<
  Record<
    FileKind,
    {
      label: string;
      defaultSensitivity: ReadingSensitivity;
      isDocument: boolean;
    }
  >
> = {
  defect: {
    label: "Defect or damage",
    defaultSensitivity: "open",
    isDocument: false,
  },
  equipment: {
    label: "Equipment",
    defaultSensitivity: "open",
    isDocument: false,
  },
  nameplate: {
    label: "Nameplate",
    defaultSensitivity: "open",
    isDocument: false,
  },
  area_overview: {
    label: "Area overview",
    defaultSensitivity: "open",
    isDocument: false,
  },
  work_in_progress: {
    label: "Work in progress",
    defaultSensitivity: "open",
    isDocument: false,
  },
  receipt: {
    label: "Receipt",
    defaultSensitivity: "internal",
    isDocument: false,
  },
  screenshot: {
    label: "Screenshot",
    defaultSensitivity: "internal",
    isDocument: false,
  },
  call_recording: {
    label: "Call recording",
    defaultSensitivity: "internal",
    isDocument: false,
  },
  listing_photo: {
    label: "Listing photo",
    defaultSensitivity: "open",
    isDocument: false,
  },
  avatar_or_logo: {
    label: "Avatar or logo",
    defaultSensitivity: "open",
    isDocument: false,
  },
  other_visual: {
    label: "Other photo or video",
    defaultSensitivity: "open",
    isDocument: false,
  },
  vendor_quote: {
    label: "Vendor quote",
    defaultSensitivity: "confidential",
    isDocument: true,
  },
  vendor_invoice: {
    label: "Vendor invoice",
    defaultSensitivity: "confidential",
    isDocument: true,
  },
  vendor_compliance: {
    label: "Vendor compliance paper",
    defaultSensitivity: "confidential",
    isDocument: true,
  },
  birdwatch_document: {
    label: "Birdwatch document",
    defaultSensitivity: "open",
    isDocument: true,
  },
  inspection_report: {
    label: "Inspection report",
    defaultSensitivity: "internal",
    isDocument: true,
  },
  home_reference: {
    label: "Home reference",
    defaultSensitivity: "open",
    isDocument: true,
  },
  contract: {
    label: "Contract",
    defaultSensitivity: "internal",
    isDocument: true,
  },
  correspondence: {
    label: "Correspondence",
    defaultSensitivity: "internal",
    isDocument: true,
  },
  other_document: {
    label: "Other document",
    defaultSensitivity: "internal",
    isDocument: true,
  },
  voice_note: {
    label: "Voice note",
    defaultSensitivity: "open",
    isDocument: false,
  },
};

/** The condition of the part of the home a photo or video shows. */
export const FILE_CONDITIONS = [
  "good",
  "fair",
  "poor",
  "critical",
  "unknown",
] as const;
export type FileCondition = (typeof FILE_CONDITIONS)[number];

/** How each condition reads to a person: the Files tab and the Digital Nest say the same words. */
export const FILE_CONDITION_LABELS: Readonly<Record<FileCondition, string>> = {
  good: "Good",
  fair: "Fair",
  poor: "Poor",
  critical: "Critical",
  unknown: "Can't tell",
};

/** Facts about a picture that are not its subject. */
export const FILE_FLAGS = [
  "blurry",
  "too_dark",
  "obstructed",
  "partial_view",
  "annotated",
  "thermal_image",
  "contains_people",
  "contains_personal_data",
] as const;
export type FileFlag = (typeof FILE_FLAGS)[number];

/** Who issued a document, relative to the home. */
export const DOCUMENT_ISSUERS = [
  "vendor",
  "birdwatch",
  "retailer",
  "homeowner",
  "manufacturer",
  "government",
  "insurer",
  "unknown",
] as const;
export type DocumentIssuer = (typeof DOCUMENT_ISSUERS)[number];

/** How much of an equipment data plate could be read. */
export const NAMEPLATE_LEGIBILITY = ["full", "partial", "none"] as const;
export type NameplateLegibility = (typeof NAMEPLATE_LEGIBILITY)[number];

/** Who set a kind or a subject. Authority: human > rule > ai. */
export const LABEL_SOURCES = ["rule", "ai", "human"] as const;
export type LabelSource = (typeof LABEL_SOURCES)[number];

export const FILE_REVIEW_STATES = [
  "none",
  "needs_review",
  "confirmed",
  "corrected",
] as const;
export type FileReviewState = (typeof FILE_REVIEW_STATES)[number];

export const FILE_REVIEW_REASONS = [
  "shared_file_looks_confidential",
  "kind_conflict",
  "low_confidence_document",
  "nameplate_unconfirmed",
] as const;
export type FileReviewReason = (typeof FILE_REVIEW_REASONS)[number];
